
// lib/getAvatarUrl.ts
import { imagePresetUrl, type ImagePreset } from "@/lib/images/presets";
import { resolveStorageUrl } from "@/lib/storage/public-url";

/**
 * Read funnel for `profiles.avatar_url`.
 *
 * Returns `null` for blank input so callers can fall back to initials, and
 * resolves a bucket-relative storage path (`avatars/8f1c-….png`) to an absolute
 * public URL — the DB is migrating off absolute Supabase URLs, and a bare path
 * handed to `<img src>` would be requested against the SITE domain (404,
 * silent). Absolute URLs, `blob:`/`data:` previews and `/local.svg` assets pass
 * through unchanged, and the resolve is idempotent.
 *
 * `preset` is OPTIONAL **on purpose, and it always will be**. This is the
 * resolve funnel for ~45 call sites, and several of them legitimately want the
 * original bytes: the OG / twitter-card tag on `app/(site)/[username]/page.tsx`
 * (crawlers get a full-size image), an upload preview, a future full-bleed
 * crop. Resizing unconditionally in here would be a silent regression with no
 * opt-out, so the no-argument call is byte-for-byte what it always was and the
 * size is something a render site *asks* for.
 *
 * Prefer `getAvatarUrl(raw, SQUARE_SM)` over
 * `imagePresetUrl(getAvatarUrl(raw) ?? "", SQUARE_SM)`: the two-argument form
 * preserves the `null` (so the initials fallback still triggers) instead of
 * routing an empty string through the URL builder.
 *
 * Rendering inside `<AvatarImage>`? Then you need NEITHER argument — that
 * primitive applies `SQUARE_SM` itself.
 *
 * @see components/ui/avatar.tsx — the primitive that presets by default
 * @see lib/images/presets.ts — the preset table
 * @see lib/storage/url.ts
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §6, §8.3
 */
export function getAvatarUrl(
  rawUrl?: string | null,
  preset?: ImagePreset,
): string | null {
  const trimmed = rawUrl?.trim();
  if (!trimmed || trimmed.length === 0) return null;
  const resolved = resolveStorageUrl(trimmed);
  return preset ? imagePresetUrl(resolved, preset) : resolved;
}
